import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GitBranch, Plus, Search, Send, CheckCircle2, ArrowRight, FileText, Wrench, Package, AlertTriangle, Loader2, Trash2, Eye, MessageSquare, Clock, User } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { useMultipleDropdowns, DropdownBadge, DropdownSelect } from "../components/shared/DropdownHooks";

const DROPDOWN_KEYS = ["ecr_statuses", "eco_statuses", "ecr_urgencies", "ecr_change_types"];

export default function ECMPage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("ecr");
  const [ecrs, setEcrs] = useState([]);
  const [ecos, setEcos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [impact, setImpact] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewForm, setReviewForm] = useState({ decision: "approve", comments: "" });
  const [commentText, setCommentText] = useState("");
  const [form, setForm] = useState({ title: "", description: "", justification: "", urgency: "normal", change_type: "design", affected_machines: "", affected_documents: "", affected_parts: "" });
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const { data: dropdowns, loading: ddLoading } = useMultipleDropdowns(DROPDOWN_KEYS);

  const fetchAll = useCallback(async () => {
    try {
      const [ecrResp, ecoResp] = await Promise.allSettled([
        axios.get(`${API}/ecr`, { headers: getAuthHeaders() }),
        axios.get(`${API}/eco`, { headers: getAuthHeaders() }),
      ]);
      if (ecrResp.status === "fulfilled") setEcrs(ecrResp.value.data || []);
      if (ecoResp.status === "fulfilled") setEcos(ecoResp.value.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      const payload = {
        ...form,
        affected_machines: form.affected_machines ? form.affected_machines.split(",").map(s => s.trim()).filter(Boolean) : [],
        affected_documents: form.affected_documents ? form.affected_documents.split(",").map(s => s.trim()).filter(Boolean) : [],
        affected_parts: form.affected_parts ? form.affected_parts.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API}/ecr`, payload, { headers: getAuthHeaders() });
      toast.success("ECR created successfully");
      setCreateOpen(false);
      setForm({ title: "", description: "", justification: "", urgency: "normal", change_type: "design", affected_machines: "", affected_documents: "", affected_parts: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create ECR"); }
  };

  const handleSubmit = async (ecrId) => {
    try {
      await axios.post(`${API}/ecr/${ecrId}/submit`, {}, { headers: getAuthHeaders() });
      toast.success("ECR submitted for review");
      fetchAll();
      if (detailItem?.id === ecrId) refreshDetail(ecrId, "ecr");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleReview = async (ecrId) => {
    try {
      await axios.post(`${API}/ecr/${ecrId}/review`, reviewForm, { headers: getAuthHeaders() });
      toast.success(`ECR ${reviewForm.decision}d`);
      setReviewOpen(null);
      fetchAll();
      if (detailItem?.id === ecrId) refreshDetail(ecrId, "ecr");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleConvertToECO = async (ecrId) => {
    try {
      await axios.post(`${API}/ecr/${ecrId}/convert-to-eco`, {}, { headers: getAuthHeaders() });
      toast.success("ECO created from approved ECR");
      fetchAll();
      setDetailItem(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleECOTransition = async (ecoId, newStatus) => {
    try {
      await axios.post(`${API}/eco/${ecoId}/transition`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`ECO transitioned to ${newStatus.replace(/_/g, " ")}`);
      fetchAll();
      if (detailItem?.id === ecoId) refreshDetail(ecoId, "eco");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleComment = async (ecrId) => {
    if (!commentText.trim()) return;
    try {
      await axios.post(`${API}/ecr/${ecrId}/comment`, { text: commentText }, { headers: getAuthHeaders() });
      setCommentText("");
      toast.success("Comment added");
      refreshDetail(ecrId, "ecr");
    } catch { toast.error("Failed to add comment"); }
  };

  const refreshDetail = async (id, type) => {
    try {
      const resp = await axios.get(`${API}/${type}/${id}`, { headers: getAuthHeaders() });
      setDetailItem({ ...resp.data, _type: type });
    } catch { /* silent */ }
  };

  const loadImpact = async (ecrId) => {
    try {
      const resp = await axios.get(`${API}/ecr/${ecrId}/impact`, { headers: getAuthHeaders() });
      setImpact(resp.data);
    } catch { toast.error("Failed to load impact analysis"); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm(`Delete this ${type.toUpperCase()}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      fetchAll();
      if (detailItem?.id === id) setDetailItem(null);
    } catch { toast.error("Failed to delete"); }
  };

  // Derive valid ECO transitions from dropdown values
  const ecoTransitionMap = {
    open: ["in_progress", "cancelled"],
    in_progress: ["pending_approval", "cancelled"],
    pending_approval: ["approved", "rejected"],
    approved: ["implementing"],
    implementing: ["completed"],
    completed: ["closed"],
  };

  const getNextECOStatus = (current) => {
    const map = { open: "in_progress", in_progress: "pending_approval", pending_approval: "approved", approved: "implementing", implementing: "completed", completed: "closed" };
    return map[current] || null;
  };

  if (loading || ddLoading) return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="flex items-center justify-center h-64" data-testid="ecm-loading"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div>
    </Layout>
  );

  const filteredEcrs = ecrs.filter(e => {
    if (filterStatus && e.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return e.title?.toLowerCase().includes(q) || e.ecr_number?.toLowerCase().includes(q) || e.initiated_by_name?.toLowerCase().includes(q);
  });

  const filteredEcos = ecos.filter(e => {
    if (filterStatus && e.status !== filterStatus) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return e.title?.toLowerCase().includes(q) || e.eco_number?.toLowerCase().includes(q) || e.created_by_name?.toLowerCase().includes(q);
  });

  // Stats
  const ecrStats = { total: ecrs.length, draft: ecrs.filter(e => e.status === "draft").length, submitted: ecrs.filter(e => e.status === "submitted" || e.status === "under_review").length, approved: ecrs.filter(e => e.status === "approved").length };
  const ecoStats = { total: ecos.length, open: ecos.filter(e => ["open", "in_progress"].includes(e.status)).length, implementing: ecos.filter(e => e.status === "implementing").length, completed: ecos.filter(e => ["completed", "closed"].includes(e.status)).length };

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="ecm-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Engineering Change Management" subtitle="ECR/ECO workflows for controlled engineering changes" testId="ecm-title" />
          <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold tracking-wider" data-testid="create-ecr-btn">
            <Plus size={14} className="mr-1.5" /> NEW ECR
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ecm-stats">
          <StatCard label="Total ECRs" value={ecrStats.total} color="#0F172A" />
          <StatCard label="Pending Review" value={ecrStats.submitted} color="#F59E0B" />
          <StatCard label="Active ECOs" value={ecoStats.open} color="#2563EB" />
          <StatCard label="Completed" value={ecoStats.completed} color="#10B981" />
        </div>

        {/* Tabs + Search + Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex">
            <button onClick={() => { setTab("ecr"); setFilterStatus(""); }} className={`px-5 py-2.5 text-xs font-bold tracking-wider border transition-colors ${tab === "ecr" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"}`} data-testid="tab-ecr">
              ECR ({ecrs.length})
            </button>
            <button onClick={() => { setTab("eco"); setFilterStatus(""); }} className={`px-5 py-2.5 text-xs font-bold tracking-wider border border-l-0 transition-colors ${tab === "eco" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"}`} data-testid="tab-eco">
              ECO ({ecos.length})
            </button>
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by title, number, or author..." className="w-full pl-9 pr-3 py-2.5 border border-[#E2E8F0] text-xs bg-white focus:border-[#2563EB] focus:outline-none transition-colors" data-testid="ecm-search" />
          </div>
          <DropdownSelect
            dropdownKey={tab === "ecr" ? "ecr_statuses" : "eco_statuses"}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-[#E2E8F0] px-3 py-2.5 text-xs bg-white min-w-[140px]"
            testId="ecm-status-filter"
            placeholder="All Statuses"
          />
        </div>

        {/* ECR List */}
        {tab === "ecr" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="ecr-list">
            {filteredEcrs.length === 0 ? (
              <EmptyState icon={<GitBranch size={36} />} message={search || filterStatus ? "No ECRs match your filters." : "No Engineering Change Requests yet. Create one to get started."} />
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {filteredEcrs.map(ecr => (
                  <ECRRow key={ecr.id} ecr={ecr} userRole={userRole} onSubmit={handleSubmit} onReview={(ecr) => { setReviewOpen(ecr); setReviewForm({ decision: "approve", comments: "" }); }} onConvert={handleConvertToECO} onView={(ecr) => { setDetailItem({ ...ecr, _type: "ecr" }); loadImpact(ecr.id); }} onDelete={() => deleteItem("ecr", ecr.id)} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ECO List */}
        {tab === "eco" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="eco-list">
            {filteredEcos.length === 0 ? (
              <EmptyState icon={<ArrowRight size={36} />} message={search || filterStatus ? "No ECOs match your filters." : "No Engineering Change Orders yet. Approve an ECR to create one."} />
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {filteredEcos.map(eco => (
                  <ECORow key={eco.id} eco={eco} nextStatus={getNextECOStatus(eco.status)} onTransition={handleECOTransition} onDelete={() => deleteItem("eco", eco.id)} onView={() => { setDetailItem({ ...eco, _type: "eco" }); }} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Create ECR Dialog ─── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg" data-testid="create-ecr-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] font-heading text-lg">New Engineering Change Request</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Describe the proposed engineering change. All fields marked * are required.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-none text-sm border-[#E2E8F0]" placeholder="Brief change description" data-testid="ecr-title" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Description</Label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm h-20 resize-none focus:border-[#2563EB] focus:outline-none" placeholder="Detailed description of the change..." data-testid="ecr-description" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Justification</Label>
              <textarea value={form.justification} onChange={e => setForm(f => ({ ...f, justification: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm h-16 resize-none focus:border-[#2563EB] focus:outline-none" placeholder="Why is this change needed?" data-testid="ecr-justification" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Urgency</Label>
                <DropdownSelect dropdownKey="ecr_urgencies" value={form.urgency} onChange={e => setForm(f => ({ ...f, urgency: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm" testId="ecr-urgency" />
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Change Type</Label>
                <DropdownSelect dropdownKey="ecr_change_types" value={form.change_type} onChange={e => setForm(f => ({ ...f, change_type: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm" testId="ecr-type" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Affected Machines (comma-separated)</Label>
              <Input value={form.affected_machines} onChange={e => setForm(f => ({ ...f, affected_machines: e.target.value }))} placeholder="SM-001, NC-003" className="mt-1 rounded-none font-mono text-xs border-[#E2E8F0]" data-testid="ecr-machines" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Affected Documents (comma-separated)</Label>
              <Input value={form.affected_documents} onChange={e => setForm(f => ({ ...f, affected_documents: e.target.value }))} placeholder="BP-101, PROC-042" className="mt-1 rounded-none font-mono text-xs border-[#E2E8F0]" data-testid="ecr-documents" />
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Affected Parts (comma-separated)</Label>
              <Input value={form.affected_parts} onChange={e => setForm(f => ({ ...f, affected_parts: e.target.value }))} placeholder="PN-8001, PN-8002" className="mt-1 rounded-none font-mono text-xs border-[#E2E8F0]" data-testid="ecr-parts" />
            </div>
            <Button onClick={handleCreate} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none font-bold tracking-wider text-xs" data-testid="ecr-submit-btn">Create ECR</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Review Dialog ─── */}
      <Dialog open={!!reviewOpen} onOpenChange={(o) => { if (!o) setReviewOpen(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-sm" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Review ECR — {reviewOpen?.ecr_number}</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Submit your review decision for this change request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Decision</Label>
              <select value={reviewForm.decision} onChange={e => setReviewForm(f => ({ ...f, decision: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm" data-testid="review-decision">
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="request_changes">Request Changes</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Comments</Label>
              <textarea value={reviewForm.comments} onChange={e => setReviewForm(f => ({ ...f, comments: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2.5 text-sm h-20 resize-none focus:border-[#2563EB] focus:outline-none" placeholder="Add review comments..." data-testid="review-comments" />
            </div>
            <Button onClick={() => handleReview(reviewOpen?.id)} className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-none font-bold tracking-wider text-xs" data-testid="review-submit-btn">Submit Review</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── Detail + Impact Dialog ─── */}
      <Dialog open={!!detailItem} onOpenChange={(o) => { if (!o) { setDetailItem(null); setImpact(null); setCommentText(""); } }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] font-heading">
              {detailItem?.ecr_number || detailItem?.eco_number} — {detailItem?.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">View details, reviews, comments, and impact analysis.</DialogDescription>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4 mt-2">
              {/* Status & Meta */}
              <div className="flex gap-2 flex-wrap items-center">
                <DropdownBadge dropdownKey={detailItem._type === "ecr" ? "ecr_statuses" : "eco_statuses"} value={detailItem.status} />
                {detailItem.change_type && <DropdownBadge dropdownKey="ecr_change_types" value={detailItem.change_type} />}
                {detailItem.urgency && <DropdownBadge dropdownKey="ecr_urgencies" value={detailItem.urgency} />}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <InfoField icon={<User size={12} />} label="Initiated By" value={detailItem.initiated_by_name || detailItem.created_by_name || "—"} />
                <InfoField icon={<Clock size={12} />} label="Created" value={new Date(detailItem.created_at).toLocaleString()} />
                {detailItem.target_date && <InfoField icon={<Clock size={12} />} label="Target Date" value={detailItem.target_date} />}
                {detailItem.ecr_number && detailItem._type === "eco" && <InfoField icon={<GitBranch size={12} />} label="Source ECR" value={detailItem.ecr_number} />}
              </div>

              {/* Description / Justification */}
              {detailItem.description && (
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Description</Label>
                  <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap">{detailItem.description}</p>
                </div>
              )}
              {detailItem.justification && (
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Justification</Label>
                  <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap">{detailItem.justification}</p>
                </div>
              )}

              {/* Affected Items */}
              {((detailItem.affected_machines || []).length > 0 || (detailItem.affected_documents || []).length > 0 || (detailItem.affected_parts || []).length > 0) && (
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Affected Items</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(detailItem.affected_machines || []).map((m, i) => <span key={`m-${i}`} className="text-[10px] px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] border border-[#BFDBFE] font-mono">{m}</span>)}
                    {(detailItem.affected_documents || []).map((d, i) => <span key={`d-${i}`} className="text-[10px] px-2 py-0.5 bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0] font-mono">{d}</span>)}
                    {(detailItem.affected_parts || []).map((p, i) => <span key={`p-${i}`} className="text-[10px] px-2 py-0.5 bg-[#FFF7ED] text-[#EA580C] border border-[#FED7AA] font-mono">{p}</span>)}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 flex-wrap border-t border-[#F1F5F9] pt-3">
                {detailItem._type === "ecr" && detailItem.status === "draft" && (
                  <Button size="sm" onClick={() => handleSubmit(detailItem.id)} className="rounded-none bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs font-bold" data-testid="detail-submit-btn"><Send size={12} className="mr-1" /> Submit for Review</Button>
                )}
                {detailItem._type === "ecr" && (detailItem.status === "submitted" || detailItem.status === "under_review") && (
                  <Button size="sm" variant="outline" onClick={() => { setReviewOpen(detailItem); setReviewForm({ decision: "approve", comments: "" }); }} className="rounded-none text-xs font-bold" data-testid="detail-review-btn"><CheckCircle2 size={12} className="mr-1" /> Review</Button>
                )}
                {detailItem._type === "ecr" && detailItem.status === "approved" && userRole === "admin" && (
                  <Button size="sm" onClick={() => handleConvertToECO(detailItem.id)} className="rounded-none bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold" data-testid="detail-convert-btn"><ArrowRight size={12} className="mr-1" /> Convert to ECO</Button>
                )}
                {detailItem._type === "eco" && getNextECOStatus(detailItem.status) && (
                  <Button size="sm" onClick={() => handleECOTransition(detailItem.id, getNextECOStatus(detailItem.status))} className="rounded-none bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-bold" data-testid="detail-transition-btn">
                    <ArrowRight size={12} className="mr-1" /> Advance to {getNextECOStatus(detailItem.status).replace(/_/g, " ")}
                  </Button>
                )}
              </div>

              {/* Reviews (ECR only) */}
              {detailItem._type === "ecr" && (detailItem.reviews || []).length > 0 && (
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Reviews</Label>
                  <div className="space-y-2 mt-1">
                    {detailItem.reviews.map((r, i) => (
                      <div key={i} className="p-2.5 border border-[#E2E8F0] bg-[#FAFAFA] text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#0F172A]">{r.reviewer_name}</span>
                          <DropdownBadge dropdownKey="ecr_statuses" value={r.decision === "approve" ? "approved" : r.decision === "reject" ? "rejected" : "changes_requested"} />
                          <span className="text-[#94A3B8] ml-auto">{new Date(r.timestamp).toLocaleString()}</span>
                        </div>
                        {r.comments && <p className="text-[#475569] mt-1">{r.comments}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments (ECR only) */}
              {detailItem._type === "ecr" && (
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Comments ({(detailItem.comments || []).length})</Label>
                  {(detailItem.comments || []).length > 0 && (
                    <div className="space-y-2 mt-1 mb-2">
                      {detailItem.comments.map((c, i) => (
                        <div key={i} className="p-2 border border-[#E2E8F0] text-xs">
                          <span className="font-bold text-[#0F172A]">{c.user_name}</span>
                          <span className="text-[#94A3B8] ml-2">{new Date(c.timestamp).toLocaleString()}</span>
                          <p className="text-[#475569] mt-0.5">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 mt-1">
                    <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add a comment..." className="flex-1 border border-[#E2E8F0] px-2.5 py-2 text-xs focus:border-[#2563EB] focus:outline-none" data-testid="comment-input" onKeyDown={e => { if (e.key === "Enter") handleComment(detailItem.id); }} />
                    <Button size="sm" onClick={() => handleComment(detailItem.id)} className="rounded-none bg-[#0F172A] text-white text-xs" data-testid="comment-submit-btn"><MessageSquare size={12} /></Button>
                  </div>
                </div>
              )}

              {/* Impact Analysis (ECR only) */}
              {detailItem._type === "ecr" && impact && (
                <div className="border-t border-[#E2E8F0] pt-3" data-testid="impact-analysis">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#0F172A] mb-2 flex items-center gap-1.5">
                    <AlertTriangle size={14} className="text-[#F59E0B]" /> Impact Analysis — {impact.total_impact} related items
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <ImpactCard icon={<FileText size={12} />} label="Blueprints" items={impact.related_blueprints || []} renderItem={(b) => `${b.original_filename} (${b.machine_number})`} />
                    <ImpactCard icon={<Wrench size={12} />} label="Work Orders" items={impact.related_work_orders || []} renderItem={(w) => `${w.title} (${w.status})`} />
                    <ImpactCard icon={<Clock size={12} />} label="Maintenance" items={impact.related_maintenance || []} renderItem={(m) => m.title} />
                    <ImpactCard icon={<Package size={12} />} label="Parts" items={impact.related_parts || []} renderItem={(p) => `${p.description} (${p.sap_number})`} />
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* ─── Sub-Components ─── */

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-[#E2E8F0] px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
      <p className="text-2xl font-heading font-black mt-0.5" style={{ color }} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
    </div>
  );
}

function EmptyState({ icon, message }) {
  return (
    <div className="p-12 text-center">
      <div className="text-[#CBD5E1] mb-2 flex justify-center">{icon}</div>
      <p className="text-sm text-[#94A3B8]">{message}</p>
    </div>
  );
}

function InfoField({ icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-[#94A3B8] mt-0.5">{icon}</span>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
        <p className="text-[#0F172A]">{value}</p>
      </div>
    </div>
  );
}

function ECRRow({ ecr, userRole, onSubmit, onReview, onConvert, onView, onDelete }) {
  const isAdmin = userRole === "admin";
  return (
    <div className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA] transition-colors group" data-testid={`ecr-${ecr.id}`}>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onView(ecr)}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-[#2563EB] font-bold">{ecr.ecr_number}</span>
          <DropdownBadge dropdownKey="ecr_statuses" value={ecr.status} />
          <DropdownBadge dropdownKey="ecr_change_types" value={ecr.change_type} />
          <DropdownBadge dropdownKey="ecr_urgencies" value={ecr.urgency} />
        </div>
        <p className="text-sm font-medium text-[#0F172A] mt-1 truncate">{ecr.title}</p>
        <p className="text-[11px] text-[#64748B]">
          By {ecr.initiated_by_name} · {new Date(ecr.created_at).toLocaleDateString()} · {(ecr.affected_machines || []).length} machines · {(ecr.comments || []).length} comments
        </p>
      </div>
      <div className="flex gap-1 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        {ecr.status === "draft" && <Button size="sm" onClick={() => onSubmit(ecr.id)} className="rounded-none bg-[#F59E0B] hover:bg-[#D97706] text-white text-[10px] font-bold" data-testid={`submit-ecr-${ecr.id}`}><Send size={11} className="mr-1" />Submit</Button>}
        {(ecr.status === "submitted" || ecr.status === "under_review") && <Button size="sm" variant="outline" onClick={() => onReview(ecr)} className="rounded-none text-[10px] font-bold" data-testid={`review-ecr-${ecr.id}`}><CheckCircle2 size={11} className="mr-1" />Review</Button>}
        {ecr.status === "approved" && isAdmin && <Button size="sm" onClick={() => onConvert(ecr.id)} className="rounded-none bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[10px] font-bold" data-testid={`convert-ecr-${ecr.id}`}><ArrowRight size={11} className="mr-1" />ECO</Button>}
        <Button size="sm" variant="outline" onClick={() => onView(ecr)} className="rounded-none text-[10px]" data-testid={`view-ecr-${ecr.id}`}><Eye size={11} /></Button>
        {isAdmin && <Button size="sm" variant="outline" onClick={onDelete} className="rounded-none border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2] text-[10px]" data-testid={`delete-ecr-${ecr.id}`}><Trash2 size={11} /></Button>}
      </div>
    </div>
  );
}

function ECORow({ eco, nextStatus, onTransition, onDelete, onView }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA] transition-colors group" data-testid={`eco-${eco.id}`}>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onView}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-[#7C3AED] font-bold">{eco.eco_number}</span>
          <DropdownBadge dropdownKey="eco_statuses" value={eco.status} />
          {eco.ecr_number && <span className="text-[10px] text-[#94A3B8] font-mono">from {eco.ecr_number}</span>}
        </div>
        <p className="text-sm font-medium text-[#0F172A] mt-1 truncate">{eco.title}</p>
        <p className="text-[11px] text-[#64748B]">
          By {eco.created_by_name} · {eco.target_date ? `Target: ${eco.target_date}` : new Date(eco.created_at).toLocaleDateString()} · {(eco.affected_machines || []).length} machines
        </p>
      </div>
      <div className="flex gap-1 shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
        {nextStatus && (
          <Button size="sm" onClick={() => onTransition(eco.id, nextStatus)} className="rounded-none bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[10px] font-bold" data-testid={`advance-eco-${eco.id}`}>
            <ArrowRight size={11} className="mr-1" /> {nextStatus.replace(/_/g, " ")}
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={onDelete} className="rounded-none border-[#FECACA] text-[#DC2626] hover:bg-[#FEF2F2] text-[10px]" data-testid={`delete-eco-${eco.id}`}><Trash2 size={11} /></Button>
      </div>
    </div>
  );
}

function ImpactCard({ icon, label, items, renderItem }) {
  return (
    <div className="p-2.5 border border-[#E2E8F0] bg-[#FAFAFA]">
      <span className="flex items-center gap-1.5 font-bold text-[#0F172A] text-xs">{icon} {label} ({items.length})</span>
      {items.length === 0 ? (
        <p className="text-[#94A3B8] mt-1 text-[11px]">None found</p>
      ) : (
        items.slice(0, 5).map((item, i) => <p key={i} className="text-[#64748B] truncate mt-0.5 text-[11px]">{renderItem(item)}</p>)
      )}
      {items.length > 5 && <p className="text-[#94A3B8] mt-0.5 text-[10px]">+{items.length - 5} more</p>}
    </div>
  );
}
